import { SagaIterator } from "redux-saga";
import { call, select, takeLatest } from "typed-redux-saga";

import { FormEngineSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { ModelSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { Localizable } from "@com.mgmtp.a12.utils/utils-localization/lib/main";
import { JsonRpc2Response } from "@com.mgmtp.a12.dataservices/dataservices-access";

import { assertNonNullable } from "../../../utilities/assert";
import { getButtonAnnotations } from "../../../utilities/modelHelpers";
import { upsertOpenRegisterRecord, isJsonRpc2ResponseArray } from "../../../utilities/rpcHelper";
import { ModulfDataSelectors } from "../../../utilities/selectors/modulfDataSelectors";
import { RESOURCE_KEYS } from "../../../localization";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { executeWithCompleteValidation, executeWithoutValidation, validate } from "../../../utilities/validationUtils";
import { UPSERT_OPENREGISTER_RECORD } from "../../../utilities/variables/customEventNames";
import { ButtonAction, createEventButtonActionMatcher } from "../../../utilities/actionUtils";
import { OPENREGISTER_PATH, MAPPING_CONFIGURATION } from "../../../utilities/variables/modulfRuntimeAnnotations";
import { executeBusy, updateWorkflowsActivityData } from "../../../utilities/workflowUtils";

import Exception = JsonRpc2Response.Exception;

const documentService = new DocumentServiceFactory().getDocumentService();

export function* upsertOpenRegisterRecordSaga(): SagaIterator<void> {
	yield* takeLatest(createEventButtonActionMatcher(UPSERT_OPENREGISTER_RECORD), function* (action: ButtonAction) {
		yield* call(executeBusy, function* () {
			const { activityId } = action.payload;
			const isValid = yield* call(executeWithCompleteValidation<boolean>, activityId, function* () {
				const messageState = yield* call(validate, activityId, () => true);
				return Object.keys(messageState).length === 0;
			});
			if (!isValid) {
				return;
			}

			yield* call(executeWithoutValidation, activityId, function* () {
				const engineState = yield* select(FormEngineSelectors.engineState(activityId));
				assertNonNullable(engineState);
				const formModel = ModelSelectors.formModel()(engineState);
				const documentModel = ModelSelectors.documentModel()(engineState);
				const { buttonPath } = action.payload.engineEvent.payload;
				const annotations = getButtonAnnotations(formModel, buttonPath);

				const openregisterGroupPath = annotations.find(it => it.name === OPENREGISTER_PATH)?.value;
				const mappingConfigurationId = annotations.find(it => it.name === MAPPING_CONFIGURATION)?.value;

				const data = yield* select(ModulfDataSelectors.selectTaskDocument(activityId));
				if (!(openregisterGroupPath && data && mappingConfigurationId)) {
					return;
				}

				const path = openregisterGroupPath.substring(openregisterGroupPath.indexOf("/root/"));

				try {
					const updatedDocument = yield* call(
						upsertOpenRegisterRecord,
						documentService.formatDates(data, documentModel),
						data.id,
						path,
						mappingConfigurationId
					);

					const parsedDocument = {
						...documentService.parseDates(updatedDocument, documentModel),
						modelId: documentModel.header.id
					};
					yield* call(updateWorkflowsActivityData, parsedDocument);
				} catch (error) {
					let message: Localizable;
					if (isJsonRpc2ResponseArray(error)) {
						message = extractExceptionMessage(error);
					} else {
						message = {
							key: RESOURCE_KEYS.features.notification.error.unexpectedError
						};
					}
					yield* call(putErrorNotification, message, error, action.payload.activityId, "openregisterUpsert");
				}
			});
		});
	});
}

function extractExceptionMessage(response: JsonRpc2Response[]): Localizable {
	const description = response
		.filter(JsonRpc2Response.error.isInstance)
		.map(it => it.error.data)
		.filter(Exception.isInstance)
		.map(it => it.description)
		.pop();
	return {
		key: RESOURCE_KEYS.features.notification.error.unexpectedError,
		args: { reason: { type: "plain", value: description?.default ?? "Unbekannter Fehler." } }
	};
}
